'use Owner';

import { Star, Quote, Heart, Sparkles, Zap, Award, Users, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";

interface Testimonial {
    id: string;
    rating: number;
    review: string;
    name: string;
    designation: string;
    avatar: string;
}

const testimonials: Testimonial[] = [
    {
        id: "1",
        rating: 5,
        review: "Our stay at the Dubai villa was absolutely incredible! The luxury amenities, stunning views, and impeccable service exceeded all our expectations. The villa was spotless and the location was perfect for exploring Dubai.",
        name: "Matthew Haugen",
        designation: "Business Executive",
        avatar: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg"
    },
    {
        id: "2",
        rating: 5,
        review: "This was our third time staying with this company and they never disappoint. The villa was beautifully furnished, the pool was perfect for the kids, and the staff went above and beyond to make our holiday special.",
        name: "Lamont Shoop",
        designation: "Travel Enthusiast",
        avatar: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg"
    },
    {
        id: "3",
        rating: 5,
        review: "The private chef service was outstanding! We enjoyed gourmet meals every day, and the concierge team arranged perfect day trips to the desert and city tours. This villa experience redefined luxury travel for us.",
        name: "Sarah Chen",
        designation: "Luxury Travel Blogger",
        avatar: "https://images.pexels.com/photos/3771069/pexels-photo-3771069.jpeg"
    },
    {
        id: "4",
        rating: 5,
        review: "18Bricks made our investment journey seamless. Their property management team is incredibly professional and the returns have exceeded our expectations. Highly recommend their services!",
        name: "David Rodriguez",
        designation: "Real Estate Investor",
        avatar: "https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg"
    },
    {
        id: "5",
        rating: 5,
        review: "The mortgage process was so smooth with 18Bricks. They guided us through every step and got us the best rates. Their financial advisory team is top-notch!",
        name: "Emily Johnson",
        designation: "First-time Buyer",
        avatar: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg"
    }
];

export default function LandlordTestimonials() {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50, scale: 0.9 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut" as const
            }
        }
    };

    return (
        <section className="py-20 relative overflow-hidden">

            <div className="container mx-auto px-4 relative z-10">
                {/* Creative Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-20"
                >
                    <div className="inline-flex items-center gap-3 bg-secondary text-white px-4 py-1 rounded-full text-sm font-medium mb-8 border border-primary/30">
                        <Heart className="w-5 h-5" />
                        <span className="font-semibold">Owner Love Stories</span>
                        {/* <Sparkles className="w-5 h-5 text-secondary animate-pulse" /> */}
                    </div>

                    <h2 className="text-3xl lg:text-4xl font-semibold text-gray-900 mb-6 leading-tight">
                        <span className="text-primary">
                            What Our
                        </span> Landlords Say
                    </h2>

                    <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
                        Real experiences from real people. Discover why thousands of Owners trust 18Bricks
                        for their property, investment, and holiday home needs.
                    </p>
                </motion.div>

                {/* Creative Testimonial Cards */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
                >
                    {testimonials.slice(0, 3).map((testimonial, index) => (
                        <motion.div
                            key={testimonial.id}
                            variants={cardVariants}
                            className="group relative"
                        >
                            {/* Main Card */}
                            <div className="relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 h-full">
                                {/* Quote Icon */}
                                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white shadow-lg">
                                    <Quote className="w-6 h-6" />
                                </div>

                                {/* Star Rating */}
                                <div className="flex items-center gap-1 mb-6 mt-4">
                                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                                        <Star
                                            key={i}
                                            className="w-5 h-5 text-yellow-400 fill-current"
                                        />
                                    ))}
                                </div>

                                {/* Review Text */}
                                <p className="text-gray-700 leading-relaxed mb-8 text-lg">
                                    "{testimonial.review}"
                                </p>

                                {/* Reviewer Information */}
                                <div className="flex items-center gap-4">
                                    {/* Profile Picture */}
                                    <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 ring-4 ring-white shadow-lg">
                                        <Image
                                            src={testimonial.avatar}
                                            alt={testimonial.name}
                                            width={64}
                                            height={64}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>

                                    {/* Name and Designation */}
                                    <div>
                                        <h4 className="font-bold text-gray-900 text-lg">
                                            {testimonial.name}
                                        </h4>
                                        <p className="text-sm text-gray-600 font-medium">
                                            {testimonial.designation}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Hover Glow Effect */}
                            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary/20 to-secondary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10"></div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
